import {diApi} from '@di-shared/api/di';
import {AggregatedSestData, CreatedMaintenanceRecordRequest, MaintenanceInfo} from '@di-shared/api/di/__generated';
import useWindowFocusTracker from '@di-shared/features/common/useWindowFocusTracker';
import useUnreadNotification from '@di-shared/features/diag/useUnreadNotification';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

export enum ExpectedReplacementPeriodType {
  DISTANCE,
  COMBINED,
  NOT_EXPECTED,
}

function getMaintenanceInfoType(maintenanceInfos: Array<MaintenanceInfo>) {
  const isDistanceType = maintenanceInfos.find(({type}) => type === MaintenanceInfo.type.DISTANCE);
  const isDayType = maintenanceInfos.find(({type}) => type === MaintenanceInfo.type.DAY);
  if (isDistanceType && isDayType) {
    return MaintenanceInfo.type.COMBINED;
  }
  if (isDayType) {
    return MaintenanceInfo.type.DAY;
  }
  return MaintenanceInfo.type.DISTANCE;
}

function getExpectedReplacementPeriodType(sourceType: AggregatedSestData.sourceType, maintenanceType: MaintenanceInfo.type) {
  if (sourceType === AggregatedSestData.sourceType.PREDICTION && maintenanceType === MaintenanceInfo.type.COMBINED) {
    return ExpectedReplacementPeriodType.COMBINED;
  }
  if (sourceType === AggregatedSestData.sourceType.PREDICTION && maintenanceType === MaintenanceInfo.type.DISTANCE) {
    return ExpectedReplacementPeriodType.DISTANCE;
  }
  return ExpectedReplacementPeriodType.NOT_EXPECTED;
}

function getReplacementStatus(a: MaintenanceInfo.status | undefined, b: MaintenanceInfo.status | undefined) {
  if (a === MaintenanceInfo.status.REQUIRED || b === MaintenanceInfo.status.REQUIRED) {
    return MaintenanceInfo.status.REQUIRED;
  } else if (a === MaintenanceInfo.status.RECOMMENDED || b === MaintenanceInfo.status.RECOMMENDED) {
    return MaintenanceInfo.status.RECOMMENDED;
  }
  return MaintenanceInfo.status.GOOD;
}

function useSestDetail(vin: string, sestCode: CreatedMaintenanceRecordRequest.sestCode) {
  const ccid = environmentStore.getState().ccid;
  const queryClient = useQueryClient();
  const {checkUnreadUserNotifications} = useUnreadNotification(vin);
  const {data, refetch} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenancesSestController.getSest), vin, ccid, sestCode],
    queryFn: () =>
      diApi.maintenancesSestController.getSest({
        vin,
        ccid,
        sestCode,
      }),
    select: (data) => data.data,
  });
  const {enableWindowFocusTracker, disableWindowFocusTracker} = useWindowFocusTracker({
    isInitiallyEnabled: false,
    onFocus: () => {
      checkUnreadUserNotifications();
      queryClient.invalidateQueries({queryKey: [getQueryKey(diApi.maintenancesSestController.getSest)]});
      disableWindowFocusTracker();
    },
  });

  const {sestName, maintenanceType, warningLight, maintenanceInfos, contentLevel, sourceType, replaceDate, replaceOdometer} = data;

  function invalidateSestData() {
    return refetch();
  }

  const {
    baseValue: stdDistance,
    remainingValue: remainingDistance,
    status: distanceReplacementStatus,
  } = maintenanceInfos?.find(({type}) => type === MaintenanceInfo.type.DISTANCE) ?? {};
  const {
    baseValue: stdDays,
    remainingValue: remainingDays,
    status: dayReplacementStatus,
  } = maintenanceInfos?.find(({type}) => type === MaintenanceInfo.type.DAY) ?? {};

  const replacementStatus = getReplacementStatus(distanceReplacementStatus, dayReplacementStatus);

  const maintenanceInfoType = getMaintenanceInfoType(maintenanceInfos);
  const expectedReplacementPeriodType = getExpectedReplacementPeriodType(sourceType, maintenanceInfoType);

  // TODO: gotoSestExchangeSelectPage 하는 일이 너무 많음 -> 분리하기
  async function gotoSestExchangeSelectPage() {
    const url = new URLSearchParams(window.location.origin + ROUTE_PATHS.DIAG_EXCHANGE_SELECT.getFullPath(vin, [sestCode]));
    url.set('isAccessDI', '1');

    WebviewUtils.openNewWebView({
      url: decodeURIComponent(url.toString()),
      isModal: true,
      isNavigationBar: false,
      isToolBar: false,
    });
    enableWindowFocusTracker();
  }

  return {
    sestName,
    maintenanceType,
    warningLight,
    expectedReplacementPeriodType,
    remainingDistance,
    remainingDays,
    stdDays,
    stdDistance,
    distanceReplacementStatus,
    dayReplacementStatus,
    replacementStatus,
    contentLevel,
    replaceDate,
    replaceOdometer,
    gotoSestExchangeSelectPage,
    invalidateSestData,
  };
}

export default useSestDetail;
